import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, Plus, Edit2, Trash2, Check, X, Settings2, Users, Briefcase, Palette, Target, Key, Save, Eye, EyeOff, Receipt, Car, MapPin, Map } from 'lucide-react';
import { toast } from 'sonner';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import ExpensePoliciesTab from '../components/admin/ExpensePoliciesTab';
import TravelModesTab from '../components/admin/TravelModesTab';
import GeoToleranceTab from '../components/admin/GeoToleranceTab';

export default function AdminPanel() {
  const { api, isAdmin } = useApp();
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [potentialBusinesses, setPotentialBusinesses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blDialogOpen, setBlDialogOpen] = useState(false);
  const [pbDialogOpen, setPbDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingBl, setEditingBl] = useState(null);
  const [editingPb, setEditingPb] = useState(null);
  const [form, setForm] = useState({ name: '', label: '', level: 5, features: [] });
  const [blForm, setBlForm] = useState({ name: '', label: '', description: '', color: '#D97706' });
  const [pbForm, setPbForm] = useState({ name: '', label: '', description: '' });
  const [llmKey, setLlmKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [googleMapsConfigured, setGoogleMapsConfigured] = useState(false);
  const [showGoogleKey, setShowGoogleKey] = useState(false);
  const [savingGoogleKey, setSavingGoogleKey] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rRes, fRes, blRes, pbRes, keyRes, gmKeyRes] = await Promise.all([
        api.getRoles(), api.getFeatures(), api.getBusinessLines(),
        api.getPotentialBusinesses(), api.getSetting('llm_api_key'),
        api.getGoogleMapsKey(),
      ]);
      setRoles(rRes.data);
      setFeatures(fRes.data);
      setBusinessLines(blRes.data);
      setPotentialBusinesses(pbRes.data);
      setLlmKey(keyRes.data.value || '');
      setGoogleMapsKey(gmKeyRes.data.value || '');
      setGoogleMapsConfigured(gmKeyRes.data.is_configured || false);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) {
    return (
      <div data-testid="admin-panel-page" className="space-y-6 fade-in">
        <Card className="border border-border/60"><CardContent className="p-12 text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-700">Admin Access Only</h2>
          <p className="text-sm text-slate-400 mt-1">Admin panel is restricted to Admin role.</p>
        </CardContent></Card>
      </div>
    );
  }

  // Role handlers
  const openCreate = () => { setEditing(null); setForm({ name: '', label: '', level: 5, features: ['dashboard'] }); setDialogOpen(true); };
  const openEdit = (role) => { setEditing(role); setForm({ name: role.name, label: role.label, level: role.level, features: [...role.features] }); setDialogOpen(true); };

  const toggleFeature = (roleId, featureKey, currentFeatures) => {
    const newFeatures = currentFeatures.includes(featureKey)
      ? currentFeatures.filter(f => f !== featureKey)
      : [...currentFeatures, featureKey];
    handleUpdateFeatures(roleId, newFeatures);
  };

  const handleUpdateFeatures = async (roleId, newFeatures) => {
    try {
      await api.updateRole(roleId, { features: newFeatures });
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, features: newFeatures } : r));
      toast.success('Permissions updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.label) { toast.error('Name and label are required'); return; }
    try {
      if (editing) {
        await api.updateRole(editing.id, { label: form.label, level: form.level, features: form.features });
      } else {
        await api.createRole(form);
      }
      toast.success(editing ? 'Role updated' : 'Role created');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Delete this custom role?')) return;
    try { await api.deleteRole(roleId); toast.success('Role deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleFormFeature = (key) => {
    setForm(prev => ({ ...prev, features: prev.features.includes(key) ? prev.features.filter(f => f !== key) : [...prev.features, key] }));
  };

  // Business line handlers
  const openCreateBl = () => { setEditingBl(null); setBlForm({ name: '', label: '', description: '', color: '#D97706' }); setBlDialogOpen(true); };
  const openEditBl = (bl) => { setEditingBl(bl); setBlForm({ name: bl.name, label: bl.label, description: bl.description || '', color: bl.color || '#D97706' }); setBlDialogOpen(true); };

  const handleSubmitBl = async () => {
    if (!blForm.name || !blForm.label) { toast.error('Name and label are required'); return; }
    try {
      if (editingBl) {
        await api.updateBusinessLine(editingBl.id, { label: blForm.label, description: blForm.description, color: blForm.color });
      } else {
        await api.createBusinessLine(blForm);
      }
      toast.success(editingBl ? 'Business line updated' : 'Business line created');
      setBlDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeleteBl = async (blId) => {
    if (!window.confirm('Delete this business line?')) return;
    try { await api.deleteBusinessLine(blId); toast.success('Business line deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  // Potential Business handlers
  const openCreatePb = () => { setEditingPb(null); setPbForm({ name: '', label: '', description: '' }); setPbDialogOpen(true); };
  const openEditPb = (pb) => { setEditingPb(pb); setPbForm({ name: pb.name, label: pb.label, description: pb.description || '' }); setPbDialogOpen(true); };

  const handleSubmitPb = async () => {
    if (!pbForm.name || !pbForm.label) { toast.error('Name and label are required'); return; }
    try {
      if (editingPb) {
        await api.updatePotentialBusiness(editingPb.id, { label: pbForm.label, description: pbForm.description });
      } else {
        await api.createPotentialBusiness(pbForm);
      }
      toast.success(editingPb ? 'Updated' : 'Created');
      setPbDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeletePb = async (pbId) => {
    if (!window.confirm('Delete this potential business?')) return;
    try { await api.deletePotentialBusiness(pbId); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  // Settings handlers
  const handleSaveKey = async () => {
    setSavingKey(true);
    try { await api.updateSetting('llm_api_key', llmKey); toast.success('API key saved'); }
    catch (e) { toast.error('Failed to save'); }
    setSavingKey(false);
  };

  const handleSaveGoogleMapsKey = async () => {
    setSavingGoogleKey(true);
    try {
      const res = await api.updateGoogleMapsKey(googleMapsKey);
      setGoogleMapsKey(res.data.value || '');
      setGoogleMapsConfigured(res.data.is_configured || false);
      toast.success('Google Maps API key saved');
    } catch (e) { toast.error('Failed to save'); }
    setSavingGoogleKey(false);
  };

  const levelColors = { 0: 'bg-red-600 text-white', 1: 'bg-amber-600 text-white', 2: 'bg-amber-500 text-white', 3: 'bg-blue-500 text-white', 4: 'bg-purple-500 text-white', 5: 'bg-emerald-500 text-white', 6: 'bg-slate-500 text-white' };

  return (
    <div data-testid="admin-panel-page" className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Admin Panel</h1>
        <p className="text-sm text-slate-500 mt-1">Manage roles, features, and business lines</p>
      </div>

      <InstructionGuide
        title="How the Admin Panel works"
        description="Configure roles, feature access, business lines, potential business categories, and system settings."
        steps={[
          'Use the Feature Matrix tab to toggle which features each role can access.',
          'Use the Roles tab to create, edit, or delete user roles and set their hierarchy level.',
          'Use the Business Lines tab to manage different business verticals (e.g., FASTag, Prepaid Cards).',
          'Use the Potential Business tab to manage lead categories visible in the Leads dropdown.',
          'Use the Settings tab to configure the LLM API key for OCR features.',
        ]}
        flow={['Select Tab', 'View / Edit', 'Save Changes']}
      />

      <Tabs defaultValue="matrix">
        <TabsList className="bg-slate-100 flex-wrap overflow-x-auto max-w-full">
          <TabsTrigger value="matrix" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
            <Settings2 className="w-4 h-4 mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">Feature </span>Matrix
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
            <Users className="w-4 h-4 mr-1 sm:mr-1.5" /> Roles
          </TabsTrigger>
          <TabsTrigger data-testid="tab-business-lines" value="business-lines" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
            <Briefcase className="w-4 h-4 mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">Business </span>Lines
          </TabsTrigger>
          <TabsTrigger data-testid="tab-potential-business" value="potential-business" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
            <Target className="w-4 h-4 mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">Potential </span>Business
          </TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
            <Key className="w-4 h-4 mr-1 sm:mr-1.5" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* Feature Access Matrix */}
        <TabsContent value="matrix">
          <Card className="border border-border/60 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-slate-900">Feature Access Matrix</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 font-semibold text-slate-500 uppercase text-xs tracking-wider sticky left-0 bg-slate-50 min-w-[180px]">Feature</th>
                    {roles.map(r => (
                      <th key={r.id} className="p-3 text-center min-w-[120px]">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${levelColors[r.level] || levelColors[6]}`}>{r.label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map(f => (
                    <tr key={f.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-3 sticky left-0 bg-white"><p className="font-semibold text-slate-800">{f.label}</p><p className="text-[10px] text-slate-400">{f.description}</p></td>
                      {roles.map(r => {
                        const has = r.features?.includes(f.key);
                        return (
                          <td key={r.id} className="p-3 text-center">
                            <button data-testid={`toggle-${r.name}-${f.key}`} onClick={() => toggleFeature(r.id, f.key, r.features || [])}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto transition-all ${has ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'}`}>
                              {has ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles List */}
        <TabsContent value="roles">
          <div className="flex justify-end mb-4">
            <Button data-testid="create-role-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
              <Plus className="w-4 h-4" /> Create Role
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map(r => (
              <Card key={r.id} data-testid={`role-card-${r.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${levelColors[r.level] || levelColors[6]}`}>L{r.level}</div>
                    <div className="flex items-center gap-1">
                      {r.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="hover:bg-amber-50"><Edit2 className="w-3.5 h-3.5" /></Button>
                      {!r.is_system && <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>}
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-slate-900">{r.label}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Role: {r.name} | Level: {r.level}</p>
                  <div className="flex flex-wrap gap-1 mt-3">{r.features?.map(f => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Business Lines - with nested tabs */}
        <TabsContent value="business-lines">
          <Tabs defaultValue="bl-list" className="w-full">
            <TabsList className="bg-slate-50 mb-4 flex-wrap overflow-x-auto max-w-full">
              <TabsTrigger data-testid="subtab-bl-list" value="bl-list" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <Briefcase className="w-4 h-4 mr-1" /> <span className="hidden xs:inline">Business </span>Lines
              </TabsTrigger>
              <TabsTrigger data-testid="subtab-expense-policies" value="expense-policies" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <Receipt className="w-4 h-4 mr-1" /> <span className="hidden xs:inline">Expense </span>Policies
              </TabsTrigger>
              <TabsTrigger data-testid="subtab-geo-tolerance" value="geo-tolerance" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <MapPin className="w-4 h-4 mr-1" /> Geo<span className="hidden xs:inline"> Tolerance</span>
              </TabsTrigger>
              <TabsTrigger data-testid="subtab-travel-modes" value="travel-modes" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
                <Car className="w-4 h-4 mr-1" /> <span className="hidden xs:inline">Travel </span>Modes
              </TabsTrigger>
            </TabsList>

            {/* Business Lines List */}
            <TabsContent value="bl-list">
              <div className="flex justify-end mb-4">
                <Button data-testid="create-bl-btn" onClick={openCreateBl} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md text-sm">
                  <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add </span>Business Line
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {businessLines.map(bl => (
                  <Card key={bl.id} data-testid={`bl-card-${bl.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all overflow-hidden">
                    <div className="h-2" style={{ backgroundColor: bl.color }} />
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: bl.color + '20', color: bl.color }}>
                          <Briefcase className="w-5 h-5" />
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditBl(bl)} className="hover:bg-amber-50"><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBl(bl.id)} className="hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-slate-900">{bl.label}</h3>
                      <p className="text-xs text-slate-400 mt-1">{bl.description || 'No description'}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <div className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: bl.color }} />
                        <span className="text-xs text-slate-500">{bl.color}</span>
                        <Badge variant="secondary" className="text-[10px] ml-auto">{bl.status || 'active'}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Expense Policies (with Fuel Rates) */}
            <TabsContent value="expense-policies">
              <ExpensePoliciesTab businessLines={businessLines} roles={roles} />
            </TabsContent>

            {/* Geo Tolerance */}
            <TabsContent value="geo-tolerance">
              <GeoToleranceTab />
            </TabsContent>

            {/* Travel Modes */}
            <TabsContent value="travel-modes">
              <TravelModesTab businessLines={businessLines} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Potential Business */}
        <TabsContent value="potential-business">
          <div className="flex justify-end mb-4">
            <Button data-testid="create-pb-btn" onClick={openCreatePb} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
              <Plus className="w-4 h-4" /> Add Potential Business
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {potentialBusinesses.map(pb => (
              <Card key={pb.id} data-testid={`pb-card-${pb.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditPb(pb)} className="hover:bg-amber-50"><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePb(pb.id)} className="hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-slate-900">{pb.label}</h3>
                  <p className="text-xs text-slate-400 mt-1">{pb.description || 'No description'}</p>
                  <Badge variant="secondary" className="text-[10px] mt-3">{pb.status || 'active'}</Badge>
                </CardContent>
              </Card>
            ))}
            {potentialBusinesses.length === 0 && (
              <Card className="border border-dashed border-border/60 col-span-full"><CardContent className="p-8 text-center">
                <Target className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No potential business categories yet. Add one to populate the Leads dropdown.</p>
              </CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="space-y-6 max-w-lg">
            <Card className="border border-border/60 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2"><Key className="w-4 h-4" /> LLM API Key</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-500">This key is used for OCR on visiting card images. Leave blank to use the default system key.</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input data-testid="llm-key-input" type={showKey ? 'text' : 'password'} value={llmKey} onChange={e => setLlmKey(e.target.value)} placeholder="sk-..." />
                    <Button variant="ghost" size="sm" onClick={() => setShowKey(!showKey)} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0">
                      {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                  <Button data-testid="save-key-btn" onClick={handleSaveKey} disabled={savingKey} className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white">
                    <Save className="w-3.5 h-3.5" /> Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border/60 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2"><Map className="w-4 h-4" /> Google Maps API Key</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-500">This key enables location autocomplete and distance calculation for travel plans. Get a key from Google Cloud Console.</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input 
                      data-testid="google-maps-key-input" 
                      type={showGoogleKey ? 'text' : 'password'} 
                      value={googleMapsKey} 
                      onChange={e => setGoogleMapsKey(e.target.value)} 
                      placeholder="AIza..." 
                    />
                    <Button variant="ghost" size="sm" onClick={() => setShowGoogleKey(!showGoogleKey)} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0">
                      {showGoogleKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                  <Button data-testid="save-google-key-btn" onClick={handleSaveGoogleMapsKey} disabled={savingGoogleKey} className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white">
                    <Save className="w-3.5 h-3.5" /> Save
                  </Button>
                </div>
                {googleMapsConfigured && (
                  <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700">
                    API Key Configured
                  </Badge>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="role-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">{editing ? 'Edit' : 'Create'} Role</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Role Name (key)</Label><Input data-testid="role-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., regional_head" disabled={!!editing} /></div>
              <div><Label>Display Label</Label><Input data-testid="role-label-input" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g., Regional Head" /></div>
            </div>
            <div><Label>Hierarchy Level (0=highest, 6=lowest)</Label><Input data-testid="role-level-input" type="number" min={0} max={10} value={form.level} onChange={e => setForm({ ...form, level: Number(e.target.value) })} /></div>
            <div>
              <Label className="mb-2 block">Feature Access</Label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {features.map(f => (
                  <label key={f.key} className="flex items-center gap-2 p-2 rounded-lg border border-border/40 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={form.features.includes(f.key)} onChange={() => toggleFormFeature(f.key)} className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
                    <span className="text-sm text-slate-700">{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-role-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">{editing ? 'Update' : 'Create'} Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Business Line Dialog */}
      <Dialog open={blDialogOpen} onOpenChange={setBlDialogOpen}>
        <DialogContent data-testid="bl-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-bold">{editingBl ? 'Edit' : 'Add'} Business Line</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name (key)</Label>
              <Input data-testid="bl-name-input" value={blForm.name} onChange={e => setBlForm({ ...blForm, name: e.target.value })} placeholder="e.g., insurance" disabled={!!editingBl} />
            </div>
            <div>
              <Label>Display Label</Label>
              <Input data-testid="bl-label-input" value={blForm.label} onChange={e => setBlForm({ ...blForm, label: e.target.value })} placeholder="e.g., Insurance Products" />
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="bl-desc-input" value={blForm.description} onChange={e => setBlForm({ ...blForm, description: e.target.value })} placeholder="Brief description" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" data-testid="bl-color-input" value={blForm.color} onChange={e => setBlForm({ ...blForm, color: e.target.value })} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" />
                <Input value={blForm.color} onChange={e => setBlForm({ ...blForm, color: e.target.value })} className="flex-1" />
                <div className="w-20 h-10 rounded-lg" style={{ backgroundColor: blForm.color }} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-bl-btn" onClick={handleSubmitBl} className="bg-amber-600 hover:bg-amber-700 text-white">{editingBl ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Potential Business Dialog */}
      <Dialog open={pbDialogOpen} onOpenChange={setPbDialogOpen}>
        <DialogContent data-testid="pb-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-bold">{editingPb ? 'Edit' : 'Add'} Potential Business</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name (key)</Label>
              <Input data-testid="pb-name-input" value={pbForm.name} onChange={e => setPbForm({ ...pbForm, name: e.target.value })} placeholder="e.g., vehicle_insurance" disabled={!!editingPb} />
            </div>
            <div>
              <Label>Display Label</Label>
              <Input data-testid="pb-label-input" value={pbForm.label} onChange={e => setPbForm({ ...pbForm, label: e.target.value })} placeholder="e.g., Vehicle Insurance" />
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="pb-desc-input" value={pbForm.description} onChange={e => setPbForm({ ...pbForm, description: e.target.value })} placeholder="Brief description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPbDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-pb-btn" onClick={handleSubmitPb} className="bg-amber-600 hover:bg-amber-700 text-white">{editingPb ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
